import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { subscribeToUsers } from '../utils/storage';
import './AdminNotifications.css';

// Bottom-right toast notifications for admins.
// Currently: alerts when a new user signs in and is awaiting role assignment.
//
// Detection: poll /users (already exposed via subscribeToUsers). Any user with
// no roles is considered pending. We persist a "seen" set in localStorage so
// existing pending users don't keep firing notifications across page reloads,
// only genuinely new sign-ins do.

const SEEN_KEY = 'tib.admin.seenPendingUserIds';
const TOAST_DURATION_MS = 12000;

function readSeen() {
  try {
    return new Set(JSON.parse(localStorage.getItem(SEEN_KEY) || '[]'));
  } catch {
    return new Set();
  }
}
function writeSeen(set) {
  try {
    localStorage.setItem(SEEN_KEY, JSON.stringify([...set]));
  } catch { /* ignore */ }
}

export default function AdminNotifications({ isAdmin }) {
  const [toasts, setToasts] = useState([]);
  const navigate = useNavigate();
  const initialisedRef = useRef(false);

  useEffect(() => {
    if (!isAdmin) return;
    let unsub = null;
    let cancelled = false;

    unsub = subscribeToUsers((users) => {
      if (cancelled) return;
      const seen = readSeen();
      const pending = users.filter((u) => (u.roles || []).length === 0);

      // First tick after mount: treat everyone currently pending as already
      // seen. Otherwise refreshing the page would re-fire toasts for users
      // that have been pending for hours.
      if (!initialisedRef.current) {
        initialisedRef.current = true;
        for (const u of pending) seen.add(u.id);
        writeSeen(seen);
        return;
      }

      const fresh = pending.filter((u) => !seen.has(u.id));
      if (fresh.length === 0) return;

      // Mark them seen right away so a second poll tick doesn't duplicate.
      for (const u of fresh) seen.add(u.id);
      writeSeen(seen);

      setToasts((prev) => [
        ...prev,
        ...fresh.map((u) => ({
          id: `${u.id}-${Date.now()}`,
          userId: u.id,
          name: u.displayName || u.email || 'New user',
          email: u.email || '',
        })),
      ]);
    });

    return () => {
      cancelled = true;
      if (typeof unsub === 'function') unsub();
    };
  }, [isAdmin]);

  function dismiss(id) {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }

  function goToRoles() {
    setToasts([]);
    navigate('/admin');
  }

  if (!isAdmin || toasts.length === 0) return null;

  return (
    <div className="admin-notifications">
      {toasts.map((t) => (
        <Toast key={t.id} toast={t} onDismiss={() => dismiss(t.id)} onAction={goToRoles} />
      ))}
    </div>
  );
}

function Toast({ toast, onDismiss, onAction }) {
  const [progress, setProgress] = useState(100);
  useEffect(() => {
    const start = Date.now();
    const tick = setInterval(() => {
      const elapsed = Date.now() - start;
      const remaining = Math.max(0, TOAST_DURATION_MS - elapsed);
      const pct = (remaining / TOAST_DURATION_MS) * 100;
      setProgress(pct);
      if (remaining === 0) {
        clearInterval(tick);
        onDismiss();
      }
    }, 100);
    return () => clearInterval(tick);
  }, [onDismiss]);

  return (
    <div className="admin-toast" role="status">
      <div className="admin-toast-progress" style={{ width: `${progress}%` }} />
      <button className="admin-toast-close" onClick={onDismiss} aria-label="Dismiss">✕</button>
      <div className="admin-toast-title">New user pending approval</div>
      <div className="admin-toast-body">
        <div className="admin-toast-name">{toast.name}</div>
        {toast.email && <div className="admin-toast-email">{toast.email}</div>}
      </div>
      <div className="admin-toast-actions">
        <button className="btn btn-primary btn-sm" onClick={onAction}>
          Go to Roles Page
        </button>
      </div>
    </div>
  );
}
